import { ArrowLeft, ArrowRight } from "lucide-react";
import { useOnboardingStore } from "./store/onboarding-store";
import { StepIndicator } from "./store/step-indicator";
import {
  PayloadBrandContext,
  StepSelectFunnels,
} from "./steps/step-1-select-funnels";
import { StepBrandConfig } from "./steps/step-2-brand-config";
import { useEffect, useState } from "react";
import {
  getBrandContext,
  postCreateHome,
  CreateHomeData,
} from "../../services/home/homeService";
import Loader from "../common/Loader";
import { toast } from "react-hot-toast";
import { dictionary } from "../../utils/dictionary";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

export interface PayloadBrandContextComplete {
  websites_info: PayloadBrandContext[];
}

export function OnboardingLayoutHomeDemo() {
  const navigate = useNavigate();
  const { language } = useAuth();
  const { data, nextStep, previousStep, reset } = useOnboardingStore();
  const { step, selectedFunnels, brandData } = data;
  const [payloadBrandContext, setPayloadBrandContext] = useState<
    PayloadBrandContext[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [loadingCreateHome, setLoadingCreateHome] = useState(false);
  const [brandName, setBrandName] = useState<string[]>([]);
  const [brandContext, setBrandContext] = useState<string[]>([]);
  const [selectedBrandName, setSelectedBrandName] = useState(
    brandName[0] || ""
  );
  const [selectedBrandContext, setSelectedBrandContext] = useState(
    brandContext[0] || ""
  );

  useEffect(() => {
    reset();
  }, []);

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <StepSelectFunnels setPayloadBrandContext={setPayloadBrandContext} />
        );
      case 2:
        return (
          <StepBrandConfig
            loading={loading}
            name={brandName}
            context={brandContext}
            selectedBrandName={selectedBrandName}
            selectedBrandContext={selectedBrandContext}
            setSelectedBrandName={setSelectedBrandName}
            setSelectedBrandContext={setSelectedBrandContext}
          />
        );
      default:
        return (
          <StepSelectFunnels setPayloadBrandContext={setPayloadBrandContext} />
        );
    }
  };

  const handleNextStep = () => {
    if (selectedFunnels.length === 0) {
      toast.error(dictionary["error-select-funnel"][language]);
      return;
    }
    setLoading(true);
    nextStep();
    setTimeout(() => {
      setBrandName([
        "ApexPath",
        "NexoraPath",
        "KyneticPath",
        "AxentroPath",
        "VyneroPath",
      ]);
      setBrandContext([
        "ApexPath es una marca de periféricos de alto rendimiento diseñada para quienes alternan entre juego y productividad sin fricciones. Nuestro enfoque es la conectividad inteligente (tri-modo estable), la ergonomía que resiste jornadas largas y la precisión ajustable al milímetro. Combinamos materiales premium con software ligero y multiplataforma para que configures macros, DPI y efectos de luz en segundos. Apostamos por la durabilidad (baterías de larga vida, recubrimientos anti-huella) y por una atención posventa rápida en LATAM. El resultado: herramientas que se sienten invisibles, responden al instante y elevan tu nivel tanto en la arena competitiva como en el flujo creativo del día a día.",
        "NexoraPath desarrolla periféricos que fusionan diseño futurista y funcionalidad extrema. Pensados para gamers y creadores, nuestros dispositivos ofrecen conectividad híbrida (2.4 GHz, Bluetooth y cable), precisión optimizada y ergonomía adaptable a cualquier estilo de uso. Incorporamos firmware de baja latencia, materiales aeroespaciales y baterías de carga rápida para que tu equipo esté listo cuando tú lo estés. Nuestro compromiso: cero interrupciones, máxima personalización y soporte técnico cercano en LATAM.",
        "En KyneticPath creemos que el rendimiento surge de la armonía entre velocidad y confort. Por eso, diseñamos periféricos con sensores de última generación, respuesta instantánea y superficies texturizadas que reducen la fatiga. Nuestro software minimalista te permite personalizar DPI, perfiles y retroiluminación en segundos, sin ocupar recursos. Con componentes premium y un servicio postventa proactivo, buscamos que cada clic y cada movimiento potencien tu desempeño, ya sea en un torneo o en tu flujo creativo diario.",
        "AxentroPath lleva la experiencia de usuario a otro nivel con periféricos diseñados para precisión quirúrgica y durabilidad extrema. Combinamos conectividad de triple modo, materiales ultraligeros y mecanismos de baja fricción para ofrecer rendimiento consistente en cualquier entorno. Nuestra plataforma de configuración multiplataforma es rápida, intuitiva y segura. Además, garantizamos soporte especializado en LATAM, porque sabemos que tu equipo es más que una herramienta: es tu ventaja competitiva.",
        "VyneroPath es una marca de periféricos de alto rendimiento diseñada para quienes alternan entre juego y productividad sin fricciones. Nuestro enfoque es la conectividad inteligente (tri-modo estable), la ergonomía que resiste jornadas largas y la precisión ajustable al milímetro. Combinamos materiales premium con software ligero y multiplataforma para que configures macros, DPI y efectos de luz en segundos. Apostamos por la durabilidad (baterías de larga vida, recubrimientos anti-huella) y por una atención posventa rápida en LATAM. El resultado: herramientas que se sienten invisibles, responden al instante y elevan tu nivel tanto en la arena competitiva como en el flujo creativo del día a día.",
      ]);
      setLoading(false);
    }, 6000);
  };

  const isLastStep = step === 2;
  const canProceed =
    step === 1 ? selectedFunnels.length > 0 : brandData.name.trim() !== "";

  const handleSubmit = () => {
    if (selectedBrandName.trim() === "" || selectedBrandContext.trim() === "") {
      toast.error(
        dictionary["error-brand-data"][language] ||
          "Por favor, completa el nombre de la marca y el contexto"
      );
      return;
    }

    setLoadingCreateHome(true);

    toast.success(dictionary["success-create-home"][language]);
    setTimeout(() => {
      navigate("/funnels?type=homes");
      setLoadingCreateHome(false);
    }, 5000);
  };

  const stepTitles = ["Seleccionar Embudos", "Configurar Marca"];

  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  useEffect(() => {
    const isBrandDataEmpty =
      selectedBrandName.trim() === "" || selectedBrandContext.trim() === "";
    const isButtonDisabled = isLastStep
      ? loadingCreateHome || isBrandDataEmpty
      : false;
    setIsButtonDisabled(isButtonDisabled);
  }, [selectedBrandName, selectedBrandContext, loadingCreateHome, isLastStep]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6 flex items-center justify-center gap-2">
        <img
          src="https://www.svgrepo.com/show/190530/magic-wand-wizard.svg"
          alt="Varita mágica"
          className="w-7 h-7"
        />
        Agrupa embudos y crea tu tienda en segundos
      </h1>

      <div className="mb-6">
        {/* Step indicators with connector lines - full width layout */}
        <div className="w-full flex items-center justify-center relative">
          {/* Connector line between steps */}
          <div className="absolute top-5 left-1/2 transform -translate-x-1/2 w-32 h-0.5 z-0">
            <div
              className={`h-0.5 w-full ${
                step > 1 ? "bg-primary-600" : "bg-gray-300"
              }`}
            ></div>
          </div>

          {[1, 2].map((stepNumber, index) => (
            <div
              key={stepNumber}
              className={`flex flex-col items-center relative z-10 ${
                index === 0 ? "mr-32" : ""
              }`}
            >
              <StepIndicator step={stepNumber} currentStep={step} />
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        {renderStepContent()}
      </div>

      <div className="flex justify-between">
        {step > 1 ? (
          <button
            type="button"
            onClick={previousStep}
            className="btn btn-secondary"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Anterior
          </button>
        ) : (
          <div></div>
        )}

        <button
          type="button"
          onClick={isLastStep ? handleSubmit : handleNextStep}
          className={`btn ${
            isButtonDisabled
              ? "btn-primary opacity-50 cursor-not-allowed"
              : "btn-primary"
          }`}
          disabled={isButtonDisabled}
        >
          {isLastStep ? (
            loadingCreateHome ? (
              <div className="flex items-center gap-2">
                <Loader />
              </div>
            ) : (
              "Crear tienda"
            )
          ) : (
            <>
              Siguiente
              <ArrowRight className="w-5 h-5 ml-2" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}
